<template>
  <div :id="`post-${post.id}`" class="forum-post" :class="computedHighlightClass">
    <div class="post-meta">
      <strong>{{ post.created_by?.username }}</strong>
      <small class="text-muted">{{ formatDate(post.created_at) }}</small>
    </div>
    <div ref="content" class="post-content ql-editor" v-html="sanitizedContent"></div>

    <div class="post-actions">
      <v-btn
        text
        dense
        size="x-small"
        style="padding: 2px 4px; font-size: 0.75rem;"
        color="primary"
        @click.prevent="$emit('reply', post.id)"
      >
        Reply
      </v-btn>

      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            text
            dense
            size="x-small"
            style="padding: 2px 4px; font-size: 0.75rem;"
            color="primary"
            @click.prevent="likeClicked"
          >
            Like ({{ post.likes_count || post.likes?.length || 0 }})
          </v-btn>
        </template>
        <span>{{ likeTooltip }}</span>
      </v-tooltip>

      <v-btn
        text
        dense
        size="x-small"
        style="padding: 2px 4px; font-size: 0.75rem;"
        color="secondary"
        v-if="canEdit"
        @click.prevent="$emit('edit', post.id)"
      >
        Edit
      </v-btn>
      <v-btn
        text
        dense
        size="x-small"
        style="padding: 2px 4px; font-size: 0.75rem;"
        color="error"
        v-if="canEdit"
        @click.prevent="$emit('delete', post.id)"
      >
        Delete
      </v-btn>
    </div>

    <div class="nested-replies" v-if="sortedReplies.length">
      <PostItem
        v-for="r in sortedReplies"
        :key="r.id"
        :post="r"
        :threadId="threadId"
        :highlightId="highlightId"
        :highlightType="highlightType"
        @reply="$emit('reply', $event)"
        @like="$emit('like', $event)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import PostItem from "./ForumPostItem.vue";
import { sanitizeContent } from '@/utils/sanitizeHelper';
import { buildResponsivePicture } from '@/utils/imageEnhancer';

export default {
  name: "PostItem",
  components: { PostItem },
  props: {
    post: { type: Object, required: true },
    threadId: { type: Number, default: null },
    highlightId: { type: [String, Number], default: null },
    highlightType: { type: String, default: "forum_like" },
  },
  setup(props, { emit }) {
    const contentRef = ref(null);
    const currentUser = window.__CURRENT_USER__ || null;

    const sanitizedContent = computed(() => sanitizeContent(props.post?.content || ''));

    const formatDate = (ds) => new Date(ds).toLocaleString();

    const canEdit = computed(() => {
      if (!currentUser) return false;
      const isAuthor =
        props.post.created_by && props.post.created_by.id === currentUser.id;
      const isManager = ["manager", "supermanager"].includes(currentUser.role);
      return isAuthor || isManager;
    });

    const sortedReplies = computed(() => {
      const arr = props.post.replies || [];
      return arr.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    });

    const likeTooltip = computed(() => {
      const likes = props.post.like_list || props.post.likes || [];
      if (!likes.length) return "No likes yet";
      const names = likes.map((l) =>
        typeof l === "string" ? l : l.user?.username || ""
      );
      return names.join(", ");
    });

    function likeClicked() {
      emit("like", props.post);
    }

    function enhanceContentDom() {
      const container = contentRef.value;
      if (!container) return;

      // Replace legacy /images/serve without /api prefix
      const imgs = Array.from(container.querySelectorAll('img'));
      imgs.forEach((img) => {
        try {
          let raw = img.getAttribute('src') || '';
          let u;
          try { u = new URL(raw, window.location.origin); } catch (_) { u = null; }

          // If pointing to non-api images/serve, fix to /api/images/serve
          if (u && u.pathname.startsWith('/images/serve')) {
            const qs = u.search || '';
            img.setAttribute('src', `/api/images/serve${qs}`);
            raw = img.getAttribute('src');
          }

          // If it's a /media/... path missing leading slash, normalize
          let finalSrc = img.getAttribute('src') || '';
          if (/^media\//i.test(finalSrc)) finalSrc = '/' + finalSrc;

          // Build <picture> for responsive delivery
          if (finalSrc) {
            const alt = img.getAttribute('alt') || '';
            const html = buildResponsivePicture(finalSrc, alt);
            const tpl = document.createElement('template');
            tpl.innerHTML = html.trim();
            const picture = tpl.content.firstChild;
            if (picture && img.parentNode) img.replaceWith(picture);
          }
        } catch (e) {
          if (process.env.NODE_ENV !== 'production') console.debug('post image enhance failed', e);
        }
      });

      // Strip editor-only attributes
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((el) => {
        if (el.removeAttribute) {
          if (el.hasAttribute('contenteditable')) el.removeAttribute('contenteditable');
          if (el.attributes) {
            Array.from(el.attributes)
              .filter(a => a.name && a.name.startsWith('data-'))
              .forEach(a => el.removeAttribute(a.name));
          }
        }
      });
    }

    const computedHighlightClass = computed(() => {
      if (props.post.id == props.highlightId) {
        return props.highlightType === "forum_tag" ? "mention-highlighted" : "highlighted";
      }
      return "";
    });

    onMounted(() => {
      nextTick(() => enhanceContentDom());
    });

    watch(sanitizedContent, () => {
      nextTick(() => enhanceContentDom());
    });

    return {
      formatDate,
      canEdit,
      sortedReplies,
      likeTooltip,
      likeClicked,
      computedHighlightClass,
      sanitizedContent,
      contentRef,
    };
  },
};
</script>

<style scoped>
.forum-post {
  border: 1px solid #eee;
  background-color: #fff;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
  position: relative;
}
.post-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #555;
}
.post-content {
  margin-bottom: 8px;
  white-space: pre-wrap;
  font-size: 0.95rem;
  color: #333;
}

/* Constrain images inserted by the Quill editor */
:deep(.post-content.ql-editor img) {
  max-width: 100% !important;
  height: auto !important;
  max-height: 250px !important;
}

.post-actions {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: 6px;
  flex-wrap: wrap;
}
.nested-replies {
  margin-left: 20px;
  border-left: 2px solid #ccc;
  padding-left: 20px;
  margin-top: 16px;
}

/* highlight animations */
.highlighted {
  animation: highlightFade 2s ease forwards;
}
@keyframes highlightFade {
  0% {
    background-color: #ddffdd;
  }
  50% {
    background-color: #ddffdd;
  }
  100% {
    background-color: transparent;
  }
}
.mention-highlighted {
  animation: mentionHighlightFade 2s ease forwards;
}
@keyframes mentionHighlightFade {
  0% {
    background-color: #cce5ff;
  }
  50% {
    background-color: #cce5ff;
  }
  100% {
    background-color: transparent;
  }
}
</style>
